import { useMemo, useState } from 'react';

import type { DealDismissReason, DealListItem } from '../../types';
import { useBulkDismiss, useBulkLogPass, useBulkPush } from '../../hooks/useDeals';
import { DismissReasonModal } from './DismissReasonModal';
import { LogPassModal } from './DealsPage.modals';
import styles from './DealsPage.module.css';

/** Slice J bulk-action toolbar, extracted verbatim from DealsPage.tsx
 *  (D4 componentization, zero behavior change). */

// Slice J §J2 — bulk-action toolbar. Renders when at least one row is
// checked. Three actions: Dismiss / Log Pass / Push Updates. Selection
// is held in the parent (DealsPage) and cleared on any filter axis change.
interface BulkActionToolbarProps {
  selected: Set<string>;
  deals: DealListItem[];
  onClear: () => void;
}

export function BulkActionToolbar({ selected, deals, onClear }: BulkActionToolbarProps) {
  const selectedItems = useMemo(
    () => deals.filter((d) => selected.has(d.deal_key)),
    [deals, selected],
  );
  const kinds = new Set(selectedItems.map((d) => d.deal_kind));
  const mixed = kinds.size > 1;
  const onlyKind = kinds.size === 1 ? selectedItems[0].deal_kind : 'candidate';

  const [dismissOpen, setDismissOpen] = useState(false);
  const [logPassOpen, setLogPassOpen] = useState(false);
  const [pushConfirmOpen, setPushConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bulkDismiss = useBulkDismiss();
  const bulkLogPass = useBulkLogPass();
  const bulkPush = useBulkPush();

  const pushFieldTotal = selectedItems.reduce((acc, d) => acc + d.pending_count, 0);

  const summarize = (results: Array<{ status: string; error: string | null; deal_key: string }>) => {
    const errors = results.filter((r) => r.status === 'error');
    if (errors.length === 0) return null;
    return `${errors.length} of ${results.length} failed: ${errors
      .slice(0, 3)
      .map((r) => `${r.deal_key}: ${r.error}`)
      .join('; ')}${errors.length > 3 ? '…' : ''}`;
  };

  return (
    <div className={styles.bulkToolbar}>
      <span className={styles.bulkCount}>{selectedItems.length} selected</span>
      <button
        type="button"
        className={styles.bulkBtn}
        onClick={() => {
          setError(null);
          setDismissOpen(true);
        }}
        disabled={bulkDismiss.isPending}
      >
        Dismiss
      </button>
      <button
        type="button"
        className={styles.bulkBtn}
        onClick={() => {
          setError(null);
          setLogPassOpen(true);
        }}
        disabled={bulkLogPass.isPending}
      >
        Log Pass
      </button>
      <button
        type="button"
        className={styles.bulkBtnPrimary}
        onClick={() => {
          setError(null);
          setPushConfirmOpen(true);
        }}
        disabled={bulkPush.isPending}
      >
        {selectedItems.some((d) => d.deal_kind === 'candidate')
          ? 'Log / Push'
          : 'Push Updates'}
      </button>
      <button
        type="button"
        className={styles.bulkBtnGhost}
        onClick={onClear}
      >
        Clear
      </button>
      {error && <span className={styles.bulkError}>{error}</span>}

      {dismissOpen && (
        <DismissReasonModal
          open={dismissOpen}
          granularity="deal"
          dealKind={mixed ? 'candidate' : (onlyKind as 'candidate' | 'affinity_opp')}
          subjectLabel={`${selectedItems.length} deal${selectedItems.length === 1 ? '' : 's'}`}
          submitting={bulkDismiss.isPending}
          onClose={() => setDismissOpen(false)}
          onConfirm={(reason, note) => {
            bulkDismiss.mutate(
              {
                deal_keys: selectedItems.map((d) => d.deal_key),
                reason: reason as DealDismissReason,
                note,
              },
              {
                onSuccess: (resp) => {
                  setDismissOpen(false);
                  onClear();
                  const summary = summarize(resp.results);
                  if (summary) setError(summary);
                },
                onError: (err: unknown) => {
                  setError(err instanceof Error ? err.message : 'Bulk dismiss failed');
                },
              },
            );
          }}
        />
      )}

      {logPassOpen && (
        <LogPassModal
          subjectLabel={`${selectedItems.length} deal${selectedItems.length === 1 ? '' : 's'}`}
          submitting={bulkLogPass.isPending}
          onClose={() => setLogPassOpen(false)}
          onSubmit={(body) => {
            bulkLogPass.mutate(
              {
                deal_keys: selectedItems.map((d) => d.deal_key),
                category: body.category,
                note: body.note,
              },
              {
                onSuccess: (resp) => {
                  setLogPassOpen(false);
                  onClear();
                  const summary = summarize(resp.results);
                  if (summary) setError(summary);
                },
                onError: (err: unknown) => {
                  setError(err instanceof Error ? err.message : 'Bulk log pass failed');
                },
              },
            );
          }}
        />
      )}

      {pushConfirmOpen && (
        <div
          className={styles.modalBackdrop}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPushConfirmOpen(false);
          }}
        >
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Push to Affinity</h3>
            <div className={styles.modalSubtitle}>
              {selectedItems.length} deal{selectedItems.length === 1 ? '' : 's'}
            </div>
            <p style={{ fontSize: 13, color: '#1A1A1A', margin: '8px 0' }}>
              About to push <strong>{selectedItems.length}</strong> deal
              {selectedItems.length === 1 ? '' : 's'} to Affinity (
              {pushFieldTotal} pending field
              {pushFieldTotal === 1 ? '' : 's'}). Candidates create new
              opportunities; existing opps receive a PATCH on their accepted
              fields plus an updated note.
            </p>
            <ul className={styles.bulkPreviewList}>
              {selectedItems.slice(0, 8).map((d) => (
                <li key={d.deal_key}>
                  <strong>{d.display_name}</strong>{' '}
                  <span style={{ color: '#888', fontSize: 11 }}>
                    ({d.deal_kind === 'candidate' ? 'new deal' : 'field update'}
                    {d.pending_count > 0 ? ` · ${d.pending_count} field${d.pending_count === 1 ? '' : 's'}` : ''})
                  </span>
                </li>
              ))}
              {selectedItems.length > 8 && (
                <li style={{ color: '#888' }}>
                  …and {selectedItems.length - 8} more
                </li>
              )}
            </ul>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.modalCancel}
                onClick={() => setPushConfirmOpen(false)}
                disabled={bulkPush.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={bulkPush.isPending}
                onClick={() => {
                  bulkPush.mutate(
                    { deal_keys: selectedItems.map((d) => d.deal_key) },
                    {
                      onSuccess: (resp) => {
                        setPushConfirmOpen(false);
                        onClear();
                        const summary = summarize(resp.results);
                        if (summary) setError(summary);
                      },
                      onError: (err: unknown) => {
                        setError(err instanceof Error ? err.message : 'Bulk push failed');
                      },
                    },
                  );
                }}
              >
                {bulkPush.isPending ? 'Pushing…' : `Push ${selectedItems.length} deal${selectedItems.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
